import { useEffect, useMemo, type CSSProperties } from 'react'
import type { ListModel } from '../GroceryList'
import { activeSections } from '../../lib/items'
import { InfoButton, InfoLine } from '../ui/InfoButton'
import { Hero, SegmentBar } from './Hero'
import { basketSegments } from '../../lib/segments'
import { RestockChips } from './RestockChips'
import { SwipeRow } from './SwipeRow'
import { QuickAdd } from './QuickAdd'
import { BasketIcon, PencilIcon, SwipeLeftIcon } from '../icons'
import { UndoToast } from './UndoToast'

// The phone's List tab (ux/02 Phone): the list to plan with. Under the frame's
// logo row and tabs (shell/AppShell): the hero number and its segments;
// Running low; the list by category, where a tap edits and swipe left
// deletes; quick add and Start shopping docked at the bottom. Ticking things
// off and the basket live in shopping mode (components/shopping).

function heroLabel(n: number) {
  return n === 0 ? 'Done for today' : n === 1 ? 'Item to get' : 'Items to get'
}

export function ShopView({ model: m, undoRef }: { model: ListModel; undoRef: React.Ref<HTMLButtonElement> }) {
  const sections = useMemo(() => activeSections(m.items, m.categories), [m.items, m.categories])
  const { close } = m.swipe

  // Scrolling closes an open row (ux/04). The page scrolls, not an inner box,
  // so iOS keeps its native scroll and status-bar tap.
  useEffect(() => {
    window.addEventListener('scroll', close, { passive: true })
    return () => window.removeEventListener('scroll', close)
  }, [close])

  return (
    <div className="ml-shop">
      <div className="ml-shop__main">
        <Hero count={m.active.length} label={heroLabel(m.active.length)} layout="phone"
          info={<InfoButton label="How the list works">
            <InfoLine icon={<PencilIcon />}>Tap an item to edit it.</InfoLine>
            <InfoLine icon={<SwipeLeftIcon />}>Swipe left to delete it. Undo brings it back.</InfoLine>
            <InfoLine icon={<BasketIcon />}>Start shopping when you’re at the store.</InfoLine>
          </InfoButton>}>
          <SegmentBar segments={basketSegments(m.trip.inBasket, m.trip.total)}
            label={`${m.trip.inBasket} of ${m.trip.total} in the basket`} />
        </Hero>
        {m.planCaption && (
          <button type="button" className="ml-plan-caption" onClick={() => m.onUpgrade()}>{m.planCaption}</button>
        )}

        <RestockChips chips={m.chips} tintFor={m.tintFor} layout="phone" onAdd={m.onChip} />


        {m.isLoading
          ? <p className="ml-caption ml-loading">Loading…</p>
          : sections.length === 0
            ? <EmptyShop inBasket={m.basket.length} />
            : sections.map(({ category, items }, n) => (
              <section key={category?.id ?? 'other'} className="ml-section"
                style={{ '--ml-tint': m.tintFor(category?.id ?? null) } as CSSProperties}>
                <h2 className="ml-section__head">
                  <span className="ml-dot" aria-hidden="true" />
                  <span className="ml-label ml-section__label">{category?.name ?? 'Other'}</span>
                  <span className="ml-section__count ml-num">{items.length}</span>
                </h2>
                <ul className="ml-rows" data-tour={n === 0 ? 'list-row' : undefined}>
                  {items.map(item => (
                    <SwipeRow key={item.id} item={item} mode="list"
                      tint={m.tintFor(item.category_id)}
                      visual={m.swipe.visual(item.id)}
                      bind={m.swipe.bind(item.id)}
                      onEdit={m.onEdit}
                      onDelete={m.onDelete} />
                  ))}
                </ul>
              </section>
            ))}
      </div>

      <div className="ml-dock">
        <UndoToast ref={undoRef} pending={m.undo.pending} layout="phone" onUndo={m.undo.undo} />
        <QuickAdd
          items={m.items}
          catalog={m.catalog}
          memory={m.categoryMemory}
          categories={m.categories}
          layout="phone"
          tintFor={m.tintFor}
          onAdd={m.onAdd}
          onPutBack={m.onPutBack}
          onEditItem={(item, intent) => m.onEdit(item.id, intent)}
          plus={m.plus}
          itemsLeft={m.itemsLeft}
          unsaved={m.unsaved}
          onDiscardUnsaved={m.onDiscardUnsaved}
          onUpgrade={m.onUpgrade}
          onAddFromBefore={m.onAddFromBefore}
          onOpenFromBefore={m.onOpenFromBefore}
          onBrowse={m.onBrowse}
        />
        <div className="ml-dock__shop">
          <button type="button" className="ml-btn ml-btn--primary ml-btn--wide" data-tour="start-shopping"
            onClick={m.onStartShopping} disabled={m.isLoading}>
            <BasketIcon size={18} />
            {m.shopLabel}
          </button>
          {m.onEndTrip && (
            <button type="button" className="ml-btn ml-btn--secondary" onClick={m.onEndTrip}>End trip</button>
          )}
        </div>
      </div>
    </div>
  )
}

function EmptyShop({ inBasket }: { inBasket: number }) {
  // The hero already shows the 0, so this is words only (ux/01: never the
  // same number twice on one screen).
  return inBasket > 0
    ? (
      <div className="ml-empty">
        <p className="ml-label ml-empty__title">Everything is in the basket</p>
        <p className="ml-caption">Continue shopping to finish the trip.</p>
      </div>
    )
    : (
      <div className="ml-empty">
        <p className="ml-label ml-empty__title">Nothing on the list</p>
        <p className="ml-caption">Add something below.</p>
      </div>
    )
}
